import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TuiMobileDialogService, TuiSheetService } from '@taiga-ui/addon-mobile';
import { TuiSheetOptions } from '@taiga-ui/addon-mobile/components/sheet/sheet-options';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ENVIRONMENT } from '@us/app-config';
import { selectUser } from '@us/web/auth/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrdersService } from '@us/web/orders/data-access';
import { tuiIsPresent } from '@taiga-ui/cdk';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrderTypeSelectorComponent } from '@us/web/orders/ui';
import {
    ApiErrorResponse,
    BusinessModel,
    FoodOptionSelectType,
    Order,
    OrderItem,
    OrderItemOption,
    OrderState,
    OrderType,
    PaymentType,
    Promotion,
    Table
} from '@us/web/shared/data-access';
import { MessageService, TrackerService } from '@us/web/shared/feature';
import {
    calcTotalAmountById,
    calcTotalItemsV2,
    calcTotalPriceV2,
    getErrorMessage,
    isFnBApp,
    orderCartItemDefault,
    PROMOTION_SUBTYPE,
    PROMOTION_SUBTYPE2,
    PROMOTION_TYPE,
    RouteUtil
} from '@us/web/shared/utils';
import {
    selectAllowLogin,
    selectShop,
    selectShopOptions,
    selectTurnOnTheStoreChainLinkFeature
} from '@us/web/shop/data-access';
import { selectSelectedShop } from '@us/web/shops/data-access';
import { cloneDeep, includes, isNil } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, tap } from 'rxjs/operators';
import { CartPageStore } from './cart-page.store';
import { CartActions } from './cart.actions';
import { refreshCart, selectCart, selectOrderId } from './cart.reducer';
import { CartService } from './cart.service';
import { TuiAlertService } from '@taiga-ui/core';
import { selectPromotionIdNotUseUserSelect, selectPromotionsUserSelect } from '@us/web/promotions/data-access';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Injectable()
export class CartEffects {
    private readonly actions$ = inject(Actions);
    private readonly cartService = inject(CartService);
    private readonly orderService = inject(OrdersService);
    private readonly translocoService = inject(TranslocoService);
    private readonly store = inject(Store);
    private readonly messageService = inject(MessageService);
    // private readonly messagingService = inject(MessagingService);
    // private readonly authService = inject(AuthService);
    private readonly navController = inject(NavController);
    private readonly sheetService = inject(TuiSheetService);
    private readonly injector = inject(Injector);
    private readonly cartStore = inject(CartPageStore);
    private readonly env = inject(ENVIRONMENT);
    private readonly router = inject(Router);
    private readonly mobileDialogService = inject(TuiMobileDialogService);
    private readonly alertService = inject(TuiAlertService);
    private readonly trackerService = inject(TrackerService);

    addToCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.addToCart),
            concatLatestFrom(() => [
                this.store.select(selectCart),
                // TODO: handle selectTurnOnTheStoreChainLinkFeature for FnB
                isFnBApp(this.env)
                    ? this.store.select(selectShop).pipe(filter(tuiIsPresent))
                    : this.store.select(selectTurnOnTheStoreChainLinkFeature).pipe(
                          filter(tuiIsPresent),
                          switchMap(chain =>
                              chain ? this.store.select(selectSelectedShop) : this.store.select(selectShop)
                          ),
                          filter(tuiIsPresent)
                      )
            ]),
            concatMap(([{ item, redirectUrl }, cart, shop]) => {
                if (includes([OrderState.Canceled, OrderState.Rejected, OrderState.Complete], cart.current_state)) {
                    return of(CartActions.clearCart({ item, shop }));
                }
                if (cart.shop && cart.shop.id !== shop?.id) {
                    //     Swal.fire({
                    //         title: 'Bạn muốn đặt món ở cửa hàng này',
                    //         text: 'Những món bạn đã đặt ở của hàng trước sẽ bị xóa khỏi giỏ hàng',
                    //         showCancelButton: true,
                    //         confirmButtonText: 'Tiếp tục',
                    //         denyButtonText: `Trờ về`,
                    //     })

                    const msg =
                        'Bạn muốn đặt món ở cửa hàng này? Những món bạn đã đặt ở của hàng trước sẽ bị xóa khỏi giỏ hàng';
                    // eslint-disable-next-line no-restricted-globals
                    if (confirm(msg)) {
                        return of(CartActions.clearCart({ item, shop }));
                    }

                    return of(CartActions.addToCartFailed());
                }

                return (
                    ((isNil(cart.order_type) || (cart.order_type === OrderType.OrderAtTheTable && !cart.order_table)) &&
                    this.env.appType === 'fnb'
                        ? this.sheetService.open(new PolymorpheusComponent(OrderTypeSelectorComponent, this.injector), {
                              closeable: true,
                              overlay: true
                          } as Partial<TuiSheetOptions>)
                        : of({ orderType: cart.order_type, table: cart.order_table })) as Observable<{
                        orderType: OrderType | null;
                        table: Table | undefined;
                    }>
                ).pipe(
                    map(({ orderType, table }) => {
                        if (
                            this.env.appType === 'fnb' &&
                            (!orderType || (orderType === OrderType.OrderAtTheTable && !table))
                        ) {
                            return CartActions.addToCartFailed();
                        }
                        let msg: string;
                        if (item.uuid) {
                            msg = 'update_success';
                        } else if (item.quantity > 0) {
                            msg = 'added_to_cart';
                        } else {
                            msg = 'removed_from_cart';
                        }
                        this.messageService.success(this.translocoService.translate(msg));
                        this.cartStore.setCurrentTabIndex(0);
                        if (redirectUrl) {
                            this.router.navigate([redirectUrl]);
                        }
                        return CartActions.addToCartSuccess({ item, shop, orderType, table });
                    })
                );
            })
        );
    });

    addToCartSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.addToCartSuccess),
                tap(({ item }) => {
                    this.trackerService.addToCart(item);
                })
            );
        },
        { dispatch: false }
    );

    // selectShop$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(StoresActions.selectStore),
    //         concatLatestFrom(() => [this.store.select(selectSelectedStore), this.store.select(selectCartShop)]),
    //         concatMap(([, shop, currentShop]) => {
    //             if (currentShop !== undefined && (shop as Shop).id !== currentShop?.id) {
    //                 // TODO: confirm
    //                 this.navController.back();
    //                 return of(CartActions.clearCart({ shop: shop as Shop }));
    //             }
    //             this.navController.back();
    //             return of(CartActions.updateCart({ shop: shop as Shop }));
    //         })
    //     );
    // });

    selectTable$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.selectTable),
            switchMap(({ tableId, shopId }) =>
                this.cartService.validateTable({ tableId, shopId }).pipe(
                    map(table => {
                        this.messageService.info(this.translocoService.translate('chose_table') + ' ' + table.name);
                        return CartActions.selectTableSuccess({ table });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.mobileDialogService
                            .open(getErrorMessage(error), {
                                label: this.translocoService.translate('notification'),
                                actions: [this.translocoService.translate('ok')]
                            })
                            .subscribe();
                        return of(CartActions.selectTableFailed({ error: error.error as ApiErrorResponse }));
                    }),
                    tap(() => {
                        this.router.navigate([], {
                            queryParams: {
                                tableId: null
                            },
                            queryParamsHandling: 'merge'
                        });
                    })
                )
            )
        );
    });

    submitCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.submitCart),
            concatLatestFrom(() => [
                this.store.select(selectCart),
                this.store.select(selectUser),
                this.store.select(selectShop),
                this.store.select(selectShopOptions),
                this.store.select(selectAllowLogin)
            ]),
            switchMap(([{ businessModel, delivery, payment_type }, cart, user, shop, config, allowLogin]) => {
                //     const accountId = user ? user.id : this.authService.getLocalAccountId();
                //     return this.messagingService
                //         .requestMessagingToken(accountId, !user?.id)
                //         .pipe(map(push_token => ({ delivery, push_token, cart, user, shop, businessModel, config })));
                // }),
                // switchMap(({ delivery, push_token, cart, user, shop, businessModel, config }) => {
                try {
                    const data: any = refreshCart(payment_type ? {
                        ...cart,
                        delivery,
                        payment_type
                    }:{
                        ...cart,
                        delivery
                    });

                    if (user) {
                        data.account_id = user.id;
                    } else if (
                        allowLogin &&
                        data.order_type !== OrderType.TakeAway &&
                        data.order_type !== OrderType.OrderAtTheTable
                    ) {
                        throw new Error('unauthorized');
                    }

                    // data.push_token = push_token;
                    data.version_code++;
                    data.items = data.items.filter((i: OrderItem) => !i.gift_category && !i.disable_gift);
                    data.items = cloneDeep(data.items).flatMap(
                        ({ version_code: versionCode, unit, combo, ...value }: OrderItem) => {
                            if (value.discount) {
                                value.price = value.price - value.discount.price;
                                unit.price = value.price - value.discount.price;
                                unit.product_price = value.price;
                            }
                            if (value.surcharge) {
                                value.price = value.price + value.surcharge.price;
                                unit.price = value.price + value.surcharge.price;
                                unit.product_price = value.price;
                            }
                            return [
                                {
                                    ...value,
                                    version_code: versionCode + 1,
                                    unit_id: unit.id,
                                    unit_name: unit.name,
                                    unit_ratio: unit.ratio
                                }
                            ].concat(
                                combo
                                    ? combo.map(
                                          ({
                                              version_code: comboVersionCode,
                                              unit: comboUnit,
                                              ...comboValue
                                          }: OrderItem) => ({
                                              ...comboValue,
                                              version_code: comboVersionCode + 1,
                                              unit_id: comboUnit.id,
                                              unit_name: comboUnit.name,
                                              unit_ratio: comboUnit.ratio,
                                              parent_uuid: value.uuid
                                          })
                                      )
                                    : []
                            );
                        }
                    );

                    const { id, name, code, address, location, email, tel } = data.shop || shop;

                    data.shop = {
                        id,
                        name,
                        code,
                        address,
                        location,
                        email,
                        tel,
                        business_model: businessModel
                    };

                    if (id) {
                        data.shop_id = id;
                    }

                    this.cartService.validateOrder(data);

                    if (data.order_type === OrderType.Pickup) {
                        if (data.receiver) {
                            data.receiver = { name: data.receiver.name, phone: data.receiver.phone };
                        } else if (user) {
                            data.receiver = { name: user.name, phone: user.phone };
                        }
                    }

                    delete data['productList'];

                    return this.cartService.submitCart(data).pipe(
                        map(response => CartActions.submitCartSuccess({ cart: response, businessModel })),
                        catchError(error => of(CartActions.submitCartFailed({ error })))
                    );
                } catch (error: any) {
                    console.error(error);
                    return of(CartActions.submitCartFailed({ error: getErrorMessage(error) }));
                }
            }),
            catchError(error => {
                console.error(error);
                return of(CartActions.submitCartFailed({ error: getErrorMessage(error) }));
            })
        );
    });

    submitCartSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.submitCartSuccess),
                tap(async ({ cart: value, businessModel }) => {
                    this.trackerService.purchase(value);
                    let paymentUrl: string | null = null;

                    if (value.payment_type === PaymentType.ZaloPay) {
                        paymentUrl = value?.payment_detail?.order_url;
                    } else if (value.payment_type === PaymentType.Payon) {
                        paymentUrl = value?.payment_detail?.data?.url_checkout;
                    } else if (value.payment_type === PaymentType.Momo) {
                        paymentUrl = value?.payment_detail?.payUrl;
                    } else if (value.payment_type === PaymentType.Vnpay) {
                        paymentUrl = value?.payment_detail?.data;
                    } else if (value.payment_type === PaymentType.PosappCard) {
                        paymentUrl = value?.payment_detail?.pay_url;
                    }

                    if (paymentUrl !== null) {
                        window.location.href = paymentUrl;
                        return;
                    }

                    const orderAtTheTable = value.order_type === OrderType.OrderAtTheTable;

                    if (!orderAtTheTable || businessModel !== BusinessModel.PrePayment || value.version_code === 1) {
                        this.messageService.success(
                            this.translocoService.translate(orderAtTheTable ? 'pos.order_placed' : 'order_placed')
                        );
                    }

                    if (!orderAtTheTable || businessModel === BusinessModel.PrePayment) {
                        await this.navController.navigateForward(RouteUtil.getOrderRouteUrl(value.order_id));
                    } else {
                        this.cartStore.setCurrentTabIndex(1);
                        await this.navController.navigateBack(RouteUtil.getCartRouteUrl());
                    }
                })
            );
        },
        { dispatch: false }
    );

    submitCartFailed$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.submitCartFailed),
                tap(async ({ error }) => {
                    if (error) {
                        if (getErrorMessage(error, '') === 'unauthorized') {
                            await this.navController.navigateForward(['/login'], {
                                queryParams: {
                                    context: 'checkout',
                                    returnUrl: '/checkout'
                                }
                            });
                        } else {
                            const message = getErrorMessage(error, this.translocoService.translate('message.error'));
                            this.messageService.error(message);
                        }
                    }
                })
            );
        },
        { dispatch: false }
    );

    pushOrderState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CartActions.pushOrderState),
            concatLatestFrom(() => this.store.select(selectOrderId)),
            concatMap(([{ new_state }, order_id]) => {
                return this.orderService.pushOrderState({ order_id, state: new_state }).pipe(
                    map(order => CartActions.pushOrderStateSuccess({ order })),
                    catchError(error => of(CartActions.pushOrderStateFailed({ error })))
                );
            }),
            catchError(error => of(CartActions.pushOrderStateFailed({ error })))
        );
    });

    removeFromCart$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(CartActions.removeFromCart),
                tap(() => {
                    this.messageService.warning(this.translocoService.translate('removed_from_cart'));
                })
            );
        },
        { dispatch: false }
    );

    applyPromotion$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    CartActions.updateCart,
                    CartActions.updateItemQuantity,
                    CartActions.addToCartSuccess,
                    CartActions.removeFromCart
                ),
                concatLatestFrom(() => [
                    this.store.select(selectCart),
                    this.store.select(selectPromotionsUserSelect),
                    this.store.select(selectPromotionIdNotUseUserSelect)
                ]),
                tap(([actions, cart, promotions_list, promotionIdNotUse]) => {
                    let cart_clone = cloneDeep(cart);
                    if (promotions_list.length) {
                        const promotions = promotions_list.filter(p => !promotionIdNotUse.includes(p.id));
                        const { items, cartNew } = this.applyPromotion(
                            cart_clone,
                            promotions,
                            calcTotalPriceV2(cart_clone.items),
                            calcTotalItemsV2(cart_clone.items)
                        );
                        cart_clone.items = items;
                        cart_clone = cartNew;
                    }
                    this.store.dispatch(CartActions.updateCartWithPromotion(cart_clone));
                })
            );
        },
        { dispatch: false }
    );

    applyPromotion = (cart: Order, promotions: Promotion[] = [], totalPrice = 0, totalItem = 0) => {
        let items: OrderItem[] = cloneDeep(cart.items);
        const promotion_ids = promotions.map((p: any) => p.id);
        const checkFoodRemove = items.filter(i => i.gift_food === 1);
        for (let i = 0; i < promotions.length; i++) {
            const promotion_detail = promotions[i].promotion_detail ?? [];
            const promotion_value = promotions[i].promotion_value ?? 0;
            const promotion_type = promotions[i].promotion_type;
            const condition_detail = promotions[i].condition_detail;
            const promotion_subtype = promotions[i].promotion_subtype;
            const promotion_subtype2 = promotions[i].promotion_subtype2;
            const promotion_id = promotions[i].id;
            const topping_flg = promotions[i].topping_flg;
            let discount_price = 0;
            let discount_percent = 0;
            let surcharge_price = 0;
            let surcharge_percent = 0;
            switch (promotion_type) {
                case PROMOTION_TYPE.BILL_SURCHARGE: {
                    // phụ thu hoá đơn
                    if (totalPrice >= condition_detail) {
                        if (promotion_subtype == PROMOTION_SUBTYPE.PERCENT) {
                            surcharge_percent = promotion_value;
                            surcharge_price = (totalPrice * surcharge_percent) / 100;
                        } else {
                            surcharge_price = promotion_value;
                        }
                    }
                    if (surcharge_price > 0) {
                        cart.surcharge = {
                            price: surcharge_price,
                            percent: surcharge_percent,
                            id: promotion_id,
                            reason: ''
                        };
                        cart.surcharge_id = promotion_id;
                    } else {
                        cart.surcharge = {
                            price: 0,
                            percent: 0,
                            id: 0,
                            reason: this.translocoService.translate('surcharge_bill')
                        };
                        cart.surcharge_id = 0;
                    }
                    break;
                }
                case PROMOTION_TYPE.GIFT_BY_PRODUCT_TYPE: {
                    // tặng món thấp nhất
                    const checkTypePromotion = promotions.filter(
                        (p: any) =>
                            [PROMOTION_TYPE.DISCOUNT_PRODUCT_TYPE].includes(p.promotion_type) &&
                            [PROMOTION_SUBTYPE.PERCENT_LOW, PROMOTION_SUBTYPE.MONEY_LOW].includes(p.promotion_subtype)
                    ); // check giảm giá món thấp
                    if (promotion_subtype2 === PROMOTION_SUBTYPE2.GIFT_PRODUCT_LOW && !checkTypePromotion.length) {
                        // nếu có giảm giá món thấp nhất thì ko vào
                        if (items.length >= promotions[i].minimum_quantity) {
                            // Khi nào đạt số lượng toi thieu trong đơn hàng thì vào
                            const itemlow = items.reduce((prev, curr) => {
                                return prev.price > 0 && !prev.extra_discount && prev.price < curr.price ? prev : curr;
                            });
                            const items_has = items.filter(i => i.gift_food === 3);
                            if (items_has.length) {
                                const index_old = items.findIndex(
                                    i => i.product_id === items_has[0].product_id && i.gift_food === 3
                                );
                                if (index_old >= 0) {
                                    items[index_old].unit.price = items[index_old].unit.product_price ?? 0;
                                    items[index_old].price = items[index_old].unit.product_price ?? 0;
                                    items[index_old].discount = {
                                        percent: 0,
                                        price: 0,
                                        id: 0,
                                        reason: ''
                                    };
                                    delete items[index_old]['extra_discount'];
                                    delete items[index_old]['gift_food'];
                                }

                                const itemlow = items.reduce((prev, curr) => {
                                    return prev.price > 0 && !prev.extra_discount && prev.price < curr.price
                                        ? prev
                                        : curr;
                                });
                                const index = items.findIndex(i => i.product_id === itemlow.product_id);
                                if (index >= 0) {
                                    if (items[index].price > 0) items[index].unit.product_price = items[index].price;
                                    items[index].unit.price = 0;
                                    items[index].price = 0;
                                    items[index].extra_discount = {
                                        promotion_type: promotion_type,
                                        promotion_subtype2: promotion_subtype2,
                                        group_local_id: ''
                                    };
                                    items[index].discount = {
                                        percent: 0,
                                        price: 0,
                                        id: promotion_id,
                                        reason: this.translocoService.translate('gift_free', {
                                            value: items[index].product_name
                                        })
                                    };
                                    items[index].gift_food = 3;
                                }
                            } else {
                                const index = items.findIndex(i => i.product_id === itemlow.product_id);
                                if (index >= 0) {
                                    if (items[index].price > 0) items[index].unit.product_price = items[index].price;
                                    items[index].unit.price = 0;
                                    items[index].price = 0;
                                    items[index].extra_discount = {
                                        promotion_type: promotion_type,
                                        promotion_subtype2: promotion_subtype2,
                                        group_local_id: ''
                                    };
                                    items[index].discount = {
                                        price: 0,
                                        percent: 0,
                                        id: promotion_id,
                                        reason: this.translocoService.translate('gift_free', {
                                            value: items[index].product_name
                                        })
                                    };
                                    items[index].gift_food = 3;
                                }
                            }
                        }
                        const item_filter = items.filter(i => !i.gift_food);
                        const item_in_cart = item_filter.map(i => i.product_id);
                        if (!item_in_cart.length) {
                            // không có sẽ xoá
                            const item_ids = items.filter(f => f.gift_food === 3);
                            if (item_ids && item_ids.length) {
                                items.splice(
                                    items.findIndex(function (i) {
                                        return i.gift_food === 3;
                                    }),
                                    1
                                );
                            }
                        }
                    }
                    break;
                }
                case PROMOTION_TYPE.DISCOUNT_PRODUCT_TYPE: {
                    if ([PROMOTION_SUBTYPE.PERCENT_LOW, PROMOTION_SUBTYPE.MONEY_LOW].includes(promotion_subtype)) {
                        if (items.length >= promotions[i].minimum_quantity) {
                            // Khi nào đạt số lượng toi thieu trong đơn hàng thì vào
                            const itemlow = items.reduce((prev, curr) => {
                                return prev.price > 0 && !prev.extra_discount && prev.price < curr.price ? prev : curr;
                            });
                            const items_has = items.filter(i => i.discount_low);
                            if (items_has.length) {
                                const index_old = items.findIndex(
                                    i => i.product_id === items_has[0].product_id && i.discount_low
                                );
                                if (index_old >= 0) {
                                    items[index_old].unit.discount = {
                                        price: 0,
                                        percent: 0
                                    };
                                    items[index_old].discount = {
                                        price: 0,
                                        percent: 0,
                                        id: 0,
                                        reason: ''
                                    };
                                    // items[index_old].unit.price = items[index_old].unit.product_price ?? 0;
                                    // items[index_old].price = items[index_old].unit.product_price ?? 0;
                                    delete items[index_old]['extra_discount'];
                                    delete items[index_old]['discount_low'];
                                }
                                const itemlow = items.reduce((prev, curr) => {
                                    return prev.price > 0 && !prev.extra_discount && prev.price < curr.price
                                        ? prev
                                        : curr;
                                });
                                if (promotion_subtype === PROMOTION_SUBTYPE.PERCENT_LOW) {
                                    discount_percent = promotion_value;
                                    discount_price = (itemlow.price * discount_percent) / 100;
                                } else {
                                    discount_price = promotion_value;
                                }
                                const index = items.findIndex(i => i.product_id === itemlow.product_id && i.price > 0);
                                if (index >= 0) {
                                    if (discount_price > 0) {
                                        items[index].unit.discount = {
                                            price: discount_price,
                                            percent: discount_percent
                                        };
                                        // items[index].unit.product_price = items[index].unit.price; // giá ban đầu
                                        // items[index].unit.price = items[index].unit.price - discount_price; // giá sau giảm
                                        // items[index].price = items[index].unit.price;
                                        items[index].extra_discount = {
                                            promotion_type: promotion_type,
                                            promotion_subtype: promotion_subtype,
                                            group_local_id: ''
                                        };
                                        items[index].discount = {
                                            price: discount_price,
                                            percent: discount_price,
                                            id: promotion_id,
                                            reason: this.translocoService.translate('discount_food', {
                                                value: items[index].product_name
                                            })
                                        };
                                        items[index].discount_low = true;
                                    }
                                }
                            } else {
                                if (promotion_subtype === PROMOTION_SUBTYPE.PERCENT_LOW) {
                                    discount_percent = promotion_value;
                                    discount_price = (itemlow.price * discount_percent) / 100;
                                } else {
                                    discount_price = promotion_value;
                                }
                                const index = items.findIndex(i => i.product_id === itemlow.product_id && i.price > 0);
                                if (index >= 0) {
                                    if (discount_price > 0) {
                                        items[index].unit.discount = {
                                            price: discount_price,
                                            percent: discount_percent
                                        };
                                        // items[index].unit.product_price = items[index].unit.price; // giá ban đầu
                                        // items[index].unit.price = items[index].unit.price - discount_price; // giá sau giảm
                                        // items[index].price = items[index].unit.price;
                                        items[index].extra_discount = {
                                            promotion_type: promotion_type,
                                            promotion_subtype: promotion_subtype,
                                            group_local_id: ''
                                        };
                                        items[index].discount = {
                                            price: discount_price,
                                            percent: discount_price,
                                            id: promotion_id,
                                            reason: this.translocoService.translate('discount_food', {
                                                value: items[index].product_name
                                            })
                                        };
                                        items[index].discount_low = true;
                                    }
                                }
                            }
                        }
                    }
                    break;
                }
            }
            items.forEach((item, index, object) => {
                for (let j = 0; j < promotion_detail.length; j++) {
                    const foods = promotion_detail[j].foods ? JSON.parse(promotion_detail[j].foods) : [];
                    const foods2 = promotion_detail[j].foods2 ? JSON.parse(promotion_detail[j].foods2) : [];
                    for (let f = 0; f < foods.length; f++) {
                        const is_category = foods[f]['is_category'] ?? false;
                        const is_valid_category = is_category && foods[f]['food_id'] == item.category_id;
                        const is_valid_product = !is_category && foods[f]['food_id'] == item.product_id;
                        const promotion_unit_ids =
                            foods[f]['unit_master_ids'] && foods[f]['unit_master_ids'].length
                                ? foods[f]['unit_master_ids'].map((u: any) => u.id)
                                : [];
                        const unit = item.unit;
                        const max_price = promotions[i]['promotion_max_price'] ?? 0;
                        switch (promotion_type) {
                            case PROMOTION_TYPE.DISCOUNT_PRODUCT_TYPE: {
                                // Giảm giá món
                                if (!is_valid_category && !is_valid_product) {
                                    continue;
                                }
                                if (promotion_unit_ids.includes(unit.id)) {
                                    if (totalPrice >= promotions[i].condition_detail) {
                                        // tổng hoá đơn lớn hơn hoặc bằng thì giảm
                                        if (promotion_subtype == PROMOTION_SUBTYPE.PERCENT) {
                                            discount_percent = promotion_value;
                                            discount_price = (item.unit.price * discount_percent) / 100;
                                        } else {
                                            discount_price = promotion_value;
                                        }
                                        // item.price > 0 loại trừ sản phẩm đã dc tặng
                                        if (!('extra_discount' in item.unit) && item.price > 0) {
                                            item.extra_discount = {
                                                promotion_type: promotion_type,
                                                promotion_subtype: promotion_subtype,
                                                group_local_id: ''
                                            };
                                        }
                                    }
                                    discount_price =
                                        max_price > 0 ? Math.min(max_price, discount_price) : discount_price;
                                    const check_amount = is_valid_category
                                        ? foods[f]['amount'] <= calcTotalAmountById(items, foods[f]['food_id'], true)
                                        : foods[f]['amount'] <= item.quantity;
                                    if (discount_price > 0 && check_amount) {
                                        if (!item.discount || (!item.discount.price && item.price > 0)) {
                                            item.unit.discount = {
                                                price: discount_price,
                                                percent: discount_percent
                                            };
                                            item.discount = {
                                                price: discount_price,
                                                percent: discount_percent,
                                                id: promotion_id,
                                                reason: this.translocoService.translate('discount_food', {
                                                    value: item.product_name
                                                })
                                            };
                                            // giảm món sẽ không thay đổi giá
                                            // item.unit.product_price = item.unit.price; // giá ban đầu
                                            // item.unit.price = item.unit.product_price - item.unit.discount.price; // giá sau giảm
                                            // item.price = item.unit.price;
                                        }
                                    } else {
                                        if (item.discount && item.discount.price && item.price > 0) {
                                            item.unit.discount = {
                                                price: 0,
                                                percent: 0
                                            };
                                            item.discount = {
                                                price: 0,
                                                percent: 0,
                                                id: 0,
                                                reason: ''
                                            };
                                            // item.unit.price = item.unit.product_price ?? 0;
                                            // item.price = item.unit.price;
                                            // delete item['extra_discount'];
                                        }
                                    }
                                }
                                break;
                            }
                            case PROMOTION_TYPE.GIFT_BY_PRODUCT_TYPE: {
                                if (promotions[i].promotion_subtype2 === PROMOTION_SUBTYPE2.GIFT_FREE) {
                                    // tặng món miễn phí
                                    const checkFood = items.filter(
                                        i => i.product_id === foods[f].food_id && i.gift_food === 1
                                    );
                                    const unitArray = checkFood.map(f => f.unit.id);
                                    const options: any = [];
                                    const data_topping =
                                        cart.productList?.filter(p => p.id === foods[f].food_id)[0] ?? null;
                                    if (topping_flg && data_topping) {
                                        data_topping.options.forEach(option => {
                                            if (option.select_type === FoodOptionSelectType.AllowSelectedMultiple) {
                                                const ids_options = option.items.map(o => o.id);
                                                for (let o = 0; o < option.items.length; o++) {
                                                    const check = options.filter((e: any) =>
                                                        ids_options.includes(e.id)
                                                    );
                                                    if (check && check.length >= option.max_choose) continue;
                                                    const random_index = Math.floor(
                                                        Math.random() * option.items.length
                                                    );
                                                    const check_options = options.filter(
                                                        (e: any) => e.id == option.items[random_index].id
                                                    );
                                                    if (!check_options.length) {
                                                        const selectedOption: OrderItemOption = {
                                                            group_id: option.id || '',
                                                            id: option.items[random_index].id,
                                                            name: option.items[random_index].name,
                                                            quantity: 1,
                                                            price: option.items[random_index].price
                                                        };
                                                        options.push(selectedOption);
                                                    }
                                                }
                                            }
                                        });
                                    }
                                    foods[f].unit_master_ids.forEach((u: any) => {
                                        const new_item: OrderItem = orderCartItemDefault(
                                            promotion_type,
                                            null,
                                            promotions[i].promotion_subtype2,
                                            foods[f].food_id,
                                            foods[f].food_name,
                                            foods[f].amount,
                                            u.id,
                                            u.name,
                                            1,
                                            promotion_id,
                                            0,
                                            this.translocoService.translate('gift_free', { value: foods[f].food_name }),
                                            options,
                                            is_category ? 1 : 0
                                        );
                                        if (totalPrice >= promotions[i].condition_detail) {
                                            if (!unitArray.includes(u.id)) {
                                                if (!checkFood.length) {
                                                    if (!is_category) {
                                                        this.messageService.success(
                                                            this.translocoService.translate('gift_free', {
                                                                value: foods[f].food_name
                                                            })
                                                        );
                                                    } else {
                                                        this.messageService.success(
                                                            this.translocoService.translate('gift_product_category', {
                                                                amount: foods[f].amount,
                                                                value: foods[f].food_name + ' (' + u.name + ')'
                                                            })
                                                        );
                                                    }
                                                }
                                                items.push(new_item);
                                            } else {
                                                const checkByCategory = items.filter(
                                                    i => i.gift_category && i.gift_food === 1
                                                );
                                                const unitArrayV2 = checkByCategory.filter(f => f.unit.id != u.id);
                                                const product_ids_not_unit = unitArrayV2.map(f => f.product_id);
                                                if (product_ids_not_unit && product_ids_not_unit.length) {
                                                    items.splice(
                                                        items.findIndex(function (i) {
                                                            return (
                                                                product_ids_not_unit.includes(i.product_id) &&
                                                                i.gift_food === 1
                                                            );
                                                        }),
                                                        1
                                                    );
                                                }
                                            }
                                        }
                                    });
                                    if (totalPrice < promotions[i].condition_detail && checkFoodRemove.length) {
                                        items = items.filter(i => i.gift_food !== 1);
                                    }
                                } else if (promotions[i].promotion_subtype2 === PROMOTION_SUBTYPE2.BUY_X_GIFT_Y) {
                                    // mua x tặng y
                                    const is_valid_category = is_category && foods[f]['food_id'] == item.category_id;
                                    const is_valid_product = !is_category && foods[f]['food_id'] == item.product_id;
                                    const check_has_amount = items.filter((i: any) => {
                                        // check nếu có 1 trong 2 sản phẩm mua đủ điều kiện
                                        for (let fmap = 0; fmap < foods.length; fmap++) {
                                            if (
                                                (is_valid_category ? i.category_id : +i.product_id) ===
                                                    foods[fmap]['food_id'] &&
                                                promotion_unit_ids.includes(i.unit.id) &&
                                                i.quantity >= foods[fmap]['amount']
                                            ) {
                                                return i;
                                            }
                                        }
                                    });
                                    const food_ids = foods.map((fv2: any) => fv2.food_id);
                                    // Xoá những sản phẩm dc tặng khi xoá sản phẩm mua
                                    const item_filter = items.filter(i => !i.gift_food);
                                    const item_in_cart = item_filter.map(i =>
                                        is_category ? i.category_id : i.product_id
                                    );
                                    if (!item_in_cart.some(i => food_ids.includes(i)) && !check_has_amount.length) {
                                        // không có sẽ xoá
                                        const food2_ids = foods2.map((f: any) => f.food_id);
                                        const checkFood = items.filter(
                                            i =>
                                                food2_ids.includes(is_category ? i.gift_category_id : i.product_id) &&
                                                i.gift_food === 2
                                        );
                                        const product_ids = checkFood.map(f =>
                                            is_category ? f.gift_category_id : f.product_id
                                        );
                                        if (product_ids && product_ids.length) {
                                            items = items.filter(
                                                i =>
                                                    !product_ids.includes(
                                                        is_category ? i.gift_category_id : i.product_id
                                                    ) && i.gift_food !== 2
                                            );
                                        }
                                    }

                                    if (!is_valid_product && !is_valid_category) {
                                        continue;
                                    }

                                    if (promotion_unit_ids.includes(unit.id)) {
                                        foods2.forEach((food2: any) => {
                                            const checkFood = items.filter(
                                                i =>
                                                    (is_category ? i.gift_category_id : i.product_id) ===
                                                        food2.food_id && i.gift_food === 2
                                            );
                                            const unitArray = checkFood.map(f => f.unit.id);
                                            const options: any = [];
                                            const data_topping =
                                                cart.productList?.filter(p => p.id === food2.food_id)[0] ?? null;
                                            if (topping_flg && data_topping) {
                                                data_topping.options.forEach(option => {
                                                    if (
                                                        option.select_type ===
                                                        FoodOptionSelectType.AllowSelectedMultiple
                                                    ) {
                                                        const ids_options = option.items.map(o => o.id);
                                                        for (let o = 0; o < option.items.length; o++) {
                                                            const check = options.filter((e: any) =>
                                                                ids_options.includes(e.id)
                                                            );
                                                            if (check && check.length >= option.max_choose) break;
                                                            const random_index = Math.floor(
                                                                Math.random() * option.items.length
                                                            );
                                                            const check_options = options.filter(
                                                                (e: any) => e.id === option.items[o].id
                                                            );
                                                            if (!check_options.length) {
                                                                const selectedOption: OrderItemOption = {
                                                                    group_id: option.id || '',
                                                                    id: option.items[random_index].id,
                                                                    name: option.items[random_index].name,
                                                                    quantity: 1,
                                                                    price: option.items[random_index].price
                                                                };
                                                                options.push(selectedOption);
                                                            }
                                                        }
                                                    }
                                                });
                                            }
                                            food2.unit_master_ids.forEach((u: any) => {
                                                const new_item: OrderItem = orderCartItemDefault(
                                                    promotion_type,
                                                    null,
                                                    promotions[i].promotion_subtype2,
                                                    food2.food_id,
                                                    food2.food_name,
                                                    food2.amount,
                                                    u.id,
                                                    u.name,
                                                    2,
                                                    promotion_id,
                                                    0,
                                                    this.translocoService.translate('gift_free', {
                                                        value: food2.food_name
                                                    }),
                                                    options,
                                                    is_category ? 1 : 0,
                                                    is_category ? food2.food_id : 0
                                                );
                                                if (
                                                    totalPrice >= promotions[i].condition_detail &&
                                                    check_has_amount.length
                                                ) {
                                                    if (!unitArray.includes(u.id)) {
                                                        if (!checkFood.length) {
                                                            this.messageService.success(
                                                                this.translocoService.translate('gift_free', {
                                                                    value: food2.food_name
                                                                })
                                                            );
                                                        }
                                                        items.push(new_item);
                                                    }
                                                    if (item.disable_gift) item.disable_gift = false;
                                                } else {
                                                    if (is_valid_category) {
                                                        const category_id = checkFood.map(f => f.gift_category_id);
                                                        items = items.filter(
                                                            i => !category_id.includes(i.gift_category_id)
                                                        );
                                                    } else {
                                                        const product_ids = checkFood.map(f => f.product_id);
                                                        if (product_ids && product_ids.length) {
                                                            items.splice(
                                                                items.findIndex(function (i) {
                                                                    return product_ids.includes(i.product_id);
                                                                }),
                                                                1
                                                            );
                                                        }
                                                    }
                                                }
                                            });
                                        });
                                    }
                                }
                                break;
                            }
                            case PROMOTION_TYPE.FOOD_SURCHARGE: {
                                // phụ thu món
                                const is_valid_category = is_category && foods[f]['food_id'] == item.category_id;
                                const is_valid_product = !is_category && foods[f]['food_id'] == item.product_id;
                                if (!is_valid_category && !is_valid_product) {
                                    continue;
                                }
                                if (promotion_unit_ids.includes(unit.id)) {
                                    if (totalPrice >= promotions[i].condition_detail) {
                                        if (promotions[i].promotion_subtype === PROMOTION_SUBTYPE.PERCENT) {
                                            surcharge_percent = promotion_value;
                                            surcharge_price = (item.unit.price * surcharge_percent) / 100;
                                        } else {
                                            surcharge_price = promotion_value;
                                        }
                                    }
                                    const check_amount = is_valid_category
                                        ? foods[f]['amount'] <= calcTotalAmountById(items, foods[f]['food_id'], true)
                                        : foods[f]['amount'] <= item.quantity;
                                    if (surcharge_price > 0 && check_amount) {
                                        if (!item.surcharge || !item.surcharge.price) {
                                            item.unit.surcharge = {
                                                price: surcharge_price,
                                                percent: surcharge_percent
                                            };
                                            // phụ thu món sẽ không thay đổi giá update 20/7
                                            // item.unit.product_price = item.unit.price; // giá ban đầu
                                            // item.unit.price = item.unit.product_price + item.unit.surcharge.price; // giá sau phụ thu
                                            // item.price = item.unit.price;
                                            item.surcharge = {
                                                price: surcharge_price,
                                                percent: surcharge_percent,
                                                id: promotion_id,
                                                reason: this.translocoService.translate('surcharge_food', {
                                                    value: item.product_name
                                                })
                                            };
                                        }
                                    } else {
                                        if (item.surcharge && item.surcharge.price) {
                                            item.unit.surcharge = {
                                                price: 0,
                                                percent: 0
                                            };
                                            // item.unit.price = item.unit.product_price ?? 0;
                                            // item.price = item.unit.price;
                                            item.surcharge = {
                                                price: 0,
                                                percent: 0,
                                                id: 0,
                                                reason: ''
                                            };
                                        }
                                    }
                                }
                                break;
                            }
                        }
                    }
                }
            });
        }
        const index_remove = items.findIndex(function (i) {
            return (
                i.gift_food &&
                [1, 2, 3].includes(i.gift_food) &&
                i.discount &&
                !promotion_ids.includes(i.discount['id'])
            );
        });
        if (index_remove >= 0) {
            items.splice(index_remove, 1);
        }
        items.sort((a, b) => {
            if (a.gift_food && b.gift_food) {
                return (b.gift_food || 0) - (a.gift_food || 0);
            } else {
                return (a.gift_food || 0) - (b.gift_food || 0);
            }
        });
        return { items: items, cartNew: cart };
    };
}
